import { newRequestId } from '../../lib/ids';
import { API_BASE, ApiError, http, isAbortError, isApiErrorBody } from '../client';
import { clearSession, getToken } from '../session';
import type { CallOptions, WriteOptions } from '../types';
import type { LookupApi, LookupResult } from './lookup';

// Paths mirror subsystems/registrar/facade/adapters/admin-api/src/admin/lookup.rs.
const rd = (o?: CallOptions) => ({ signal: o?.signal });
const wr = (o?: WriteOptions) => ({ signal: o?.signal, idempotencyKey: o?.idempotencyKey, notifyCustomer: o?.notifyCustomer });

/** `POST /lookup/check` with `Accept: application/x-ndjson`: one JSON result per line. */
async function streamCheck(body: unknown, onResult: (r: LookupResult) => void, opts?: CallOptions): Promise<void> {
  const requestId = newRequestId();
  const headers = new Headers({ Accept: 'application/x-ndjson', 'Content-Type': 'application/json', 'X-Request-Id': requestId });
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/lookup/check`, { method: 'POST', headers, body: JSON.stringify(body), signal: opts?.signal, credentials: 'same-origin' });
  } catch (cause) {
    if (isAbortError(cause)) throw cause;
    throw new ApiError({ code: 'NETWORK_ERROR', message: 'The server could not be reached.', requestId, status: 0, cause });
  }
  if (!res.ok) {
    if (res.status === 401 && token) clearSession('expired');
    const payload: unknown = await res.json().catch(() => undefined);
    if (isApiErrorBody(payload)) {
      throw new ApiError({ code: payload.error.code, message: payload.error.message, requestId: payload.error.request_id || requestId, status: res.status, details: payload.error.details });
    }
    throw new ApiError({ code: `HTTP_${res.status}`, message: res.statusText || `Request failed with status ${res.status}`, requestId, status: res.status });
  }
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  for (;;) {
    const { done, value } = await reader.read();
    buffered += decoder.decode(value, { stream: !done });
    let nl = buffered.indexOf('\n');
    while (nl >= 0) {
      const line = buffered.slice(0, nl).trim();
      buffered = buffered.slice(nl + 1);
      if (line) onResult(JSON.parse(line) as LookupResult);
      nl = buffered.indexOf('\n');
    }
    if (done) break;
  }
  if (buffered.trim()) onResult(JSON.parse(buffered) as LookupResult);
}

export const lookupHttp: LookupApi = {
  lookupCheck: (body, onResult, opts) => streamCheck(body, onResult, opts),
  lookupWhois: (domain, opts) => http.get('api', '/lookup/whois', { query: { domain }, ...rd(opts) }),
  importWhoisPkcs12: (body, opts) => http.post('api', '/provider-settings/VNNIC_WHOIS/certificates/pkcs12', body, wr(opts)),
};
